/**
 * Spec SASADM-0086 (U2) — per-tenant Turnstile / Captcha admin section
 * (saas-auth SASATH-0149 config CRUD · SASATH-0151 Cloudflare automation ·
 * SASATH-0152 per-tenant enforce).
 *
 * SUPER_ADMIN-only grouping rendered on the Settings tab, above the GHL
 * integration section. Structurally mirrors TenantGhlIntegration: the caller
 * (TenantSettings) gates on `operator?.isSuperAdmin`; this component additionally
 * degrades to a contained "Super admin required" state if either self-fetch
 * returns 403. It self-fetches the tenant's captcha config AND the Cloudflare
 * widget state after mount, with isolated loading/empty/error/403 states — it is
 * NOT loaded in tenantWorkspaceLoader.
 *
 * SECRET HYGIENE (load-bearing). The Turnstile secret is a write-only credential:
 * the read never returns the plaintext (only `secretKeySet` / `secretKeySuffix`). The
 * site key and secret are Cloudflare-managed (via Provision / Rotate) and shown
 * here as read-only displays — the secret only ever as its masked suffix. `Save`
 * never sends `secretKey`; Rotate is the only way to change the secret and Remove
 * (DELETE) is the only clear path.
 */
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  FormControlLabel,
  Link,
  Paper,
  Stack,
  Switch,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { useEffect, useState, type FormEvent } from 'react';
import { apiAdmin } from '@/api/admin';
import { ApiError } from '@/api/errors';
import type {
  CaptchaConfigDto,
  CaptchaWidgetDto,
  SetCaptchaConfigRequest,
} from '@/api/types';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { CopyToClipboardButton } from '@/components/CopyToClipboardButton';
import { ErrorBanner } from '@/components/ErrorBanner';
import { env } from '@/config/env';
import { useCopy } from '@/hooks/useCopy';
import { useSnackbar } from '@/components/Snackbar';

type EnforceChoice = 'inherit' | 'observe' | 'enforce';
type Translate = (key: string, fallback?: string, values?: Record<string, string | number>) => string;

function isForbidden(err: unknown): boolean {
  return err instanceof ApiError && err.status === 403;
}

function mapCaptchaError(err: unknown, t: Translate): string | null {
  if (!(err instanceof ApiError)) return null;
  switch (err.code) {
    case 'TOO_MANY_HOSTNAMES':
      return t(
        'captcha.errors.tooManyHostnames',
        'This tenant has more than 10 APP/LOGIN hostnames — a Turnstile widget supports at most 10. Remove some hostnames and try again.',
      );
    case 'NO_HOSTNAMES':
      return t(
        'captcha.errors.noHostnames',
        'This tenant has no APP or LOGIN hostname to seed the Turnstile widget. Add a hostname first.',
      );
    case 'WIDGET_NOT_PROVISIONED':
      return t(
        'captcha.errors.widgetNotProvisioned',
        'No Turnstile widget is provisioned for this tenant yet. Provision a widget first.',
      );
    default:
      return null;
  }
}

function enforceToChoice(value: boolean | null | undefined): EnforceChoice {
  if (value === null || value === undefined) return 'inherit';
  return value ? 'enforce' : 'observe';
}

function choiceToEnforce(choice: EnforceChoice): boolean | null {
  if (choice === 'inherit') return null;
  return choice === 'enforce';
}

export function TenantCaptcha({ tenantId }: { tenantId: number }) {
  const { t } = useCopy();
  const snack = useSnackbar();
  // `undefined` = not yet loaded; `null` = loaded but not configured (404).
  const [config, setConfig] = useState<CaptchaConfigDto | null | undefined>(undefined);
  // `undefined` = not yet loaded; `null` = the widget read failed (kept non-fatal).
  const [widget, setWidget] = useState<CaptchaWidgetDto | null | undefined>(undefined);
  const [forbidden, setForbidden] = useState(false);
  const [loadError, setLoadError] = useState<ApiError | null>(null);

  // Editable policy state. The site key + secret are read-only (Cloudflare-managed).
  const [enabled, setEnabled] = useState(false);
  const [enforceChoice, setEnforceChoice] = useState<EnforceChoice>('inherit');
  const [saveError, setSaveError] = useState<ApiError | null>(null);
  const [saving, setSaving] = useState(false);
  const [provisioning, setProvisioning] = useState(false);
  const [rotating, setRotating] = useState(false);
  const [confirmRemove, setConfirmRemove] = useState(false);
  const [removing, setRemoving] = useState(false);

  // "Configured" is robust whether the backend returns 404 (→ null) or an
  // all-empty default object for an unconfigured tenant.
  const configured =
    config != null &&
    (config.enabled || config.siteKey != null || config.secretKeySet || config.enforce != null);
  const provisioned = widget?.provisioned === true;

  // Sync the editable policy controls from the loaded config.
  function syncForm(next: CaptchaConfigDto | null) {
    setEnabled(next?.enabled ?? false);
    setEnforceChoice(enforceToChoice(next?.enforce ?? null));
  }

  async function load() {
    setLoadError(null);
    try {
      const [cfg, wid] = await Promise.all([
        apiAdmin.captcha.get(tenantId),
        apiAdmin.captcha.getWidget(tenantId),
      ]);
      setConfig(cfg);
      setWidget(wid);
      setForbidden(false);
      syncForm(cfg);
    } catch (err) {
      if (isForbidden(err)) {
        setForbidden(true);
        setConfig(null);
        setWidget(null);
        return;
      }
      if (err instanceof ApiError) setLoadError(err);
      setConfig(null);
      setWidget(null);
    }
  }

  useEffect(() => {
    void (async () => {
      await load();
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenantId]);

  async function handleSave(e: FormEvent) {
    e.preventDefault();
    setSaveError(null);
    setSaving(true);
    try {
      // Persist only the operator-owned policy. The site key is passed through
      // read-only so the PUT never blanks it; secretKey is NEVER sent — the secret
      // is managed only via Rotate secret / Remove.
      const req: SetCaptchaConfigRequest = {
        enabled,
        siteKey: config?.siteKey ?? '',
        enforce: choiceToEnforce(enforceChoice),
      };
      const saved = await apiAdmin.captcha.set(tenantId, req);
      setConfig(saved);
      syncForm(saved);
      snack.success(t('captcha.saved', 'Captcha settings saved.'));
    } catch (err) {
      if (isForbidden(err)) {
        setForbidden(true);
        return;
      }
      if (err instanceof ApiError) setSaveError(err);
      else snack.error(t('errors.generic', 'Something went wrong.'));
    } finally {
      setSaving(false);
    }
  }

  function reportActionError(err: unknown) {
    if (isForbidden(err)) {
      setForbidden(true);
      return;
    }
    const friendly = mapCaptchaError(err, t);
    if (friendly) {
      snack.error(friendly);
    } else if (err instanceof ApiError) {
      snack.error(t(`errors.codes.${err.code}`, err.details.message ?? err.code));
    } else {
      snack.error(t('errors.generic', 'Something went wrong.'));
    }
  }

  async function handleProvision() {
    setProvisioning(true);
    try {
      await apiAdmin.captcha.provision(tenantId);
      // Re-read config + widget so the site key + secret-set indicator populate.
      await load();
      snack.success(t('captcha.provisioned', 'Turnstile widget provisioned.'));
    } catch (err) {
      reportActionError(err);
    } finally {
      setProvisioning(false);
    }
  }

  async function handleRotate() {
    setRotating(true);
    try {
      await apiAdmin.captcha.rotateSecret(tenantId);
      // Re-read — the masked secret changes.
      await load();
      snack.success(t('captcha.secretRotated', 'Widget secret rotated.'));
    } catch (err) {
      reportActionError(err);
    } finally {
      setRotating(false);
    }
  }

  async function handleRemove() {
    setRemoving(true);
    try {
      await apiAdmin.captcha.remove(tenantId);
      snack.success(t('captcha.removed', 'Captcha config removed.'));
      setConfirmRemove(false);
      setConfig(null);
      syncForm(null);
    } catch (err) {
      setConfirmRemove(false);
      reportActionError(err);
    } finally {
      setRemoving(false);
    }
  }

  if (forbidden) {
    return (
      <Paper variant="outlined" sx={{ p: 2, mt: 4 }}>
        <Typography variant="h3" component="h3" gutterBottom>
          {t('captcha.title', 'Turnstile / Captcha')}
        </Typography>
        <Alert severity="warning">
          {t('captcha.superAdminRequired', 'Super admin required to manage Captcha settings.')}
        </Alert>
      </Paper>
    );
  }

  return (
    <Paper variant="outlined" sx={{ p: 2, mt: 4 }}>
      <Typography variant="h3" component="h3" gutterBottom>
        {t('captcha.title', 'Turnstile / Captcha')}
      </Typography>
      <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
        {t(
          'captcha.explanation',
          "Cloudflare Turnstile protection for this tenant's login and app hostnames. The secret is write-only — it is never displayed again after you save it.",
        )}
      </Typography>
      <ErrorBanner error={loadError} />
      {config === undefined ? (
        <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
          <CircularProgress size={16} />
          <Typography sx={{ color: 'text.secondary' }}>{t('common.loading', 'Loading')}</Typography>
        </Stack>
      ) : (
        <>
          {widget?.widgetUnavailable ? (
            <Alert severity="warning" sx={{ mb: 2 }}>
              {t(
                'captcha.widget.unavailable',
                'Cloudflare widget temporarily unavailable — try again shortly.',
              )}
            </Alert>
          ) : null}

          <ErrorBanner error={saveError} />
          <Box component="form" onSubmit={(e) => void handleSave(e)} noValidate>
            <Stack
              direction="row"
              spacing={3}
              sx={{ alignItems: 'flex-start', flexWrap: 'wrap', rowGap: 2, mb: 2 }}
            >
              <FormControlLabel
                control={
                  <Switch checked={enabled} onChange={(e) => setEnabled(e.target.checked)} />
                }
                label={t('captcha.fields.enabled', 'Enabled')}
              />
              <Box>
                <Typography
                  variant="caption"
                  sx={{ color: 'text.secondary', display: 'block', mb: 0.5 }}
                >
                  {t('captcha.fields.enforce', 'Enforcement')}
                </Typography>
                <ToggleButtonGroup
                  exclusive
                  size="small"
                  value={enforceChoice}
                  onChange={(_e, val) => {
                    if (val !== null) setEnforceChoice(val as EnforceChoice);
                  }}
                  aria-label={t('captcha.fields.enforce', 'Enforcement')}
                >
                  <ToggleButton value="inherit">
                    {t('captcha.enforce.inherit', 'Inherit global')}
                  </ToggleButton>
                  <ToggleButton value="observe">
                    {t('captcha.enforce.observe', 'Observe')}
                  </ToggleButton>
                  <ToggleButton value="enforce">
                    {t('captcha.enforce.enforce', 'Enforce')}
                  </ToggleButton>
                </ToggleButtonGroup>
                <Typography
                  variant="caption"
                  sx={{ color: 'text.secondary', display: 'block', mt: 0.5 }}
                >
                  {t(
                    'captcha.fields.enforceHelper',
                    'Inherit the global policy, observe only (no block), or enforce for this tenant.',
                  )}
                </Typography>
              </Box>
            </Stack>

            <Box
              sx={{
                bgcolor: 'action.hover',
                borderRadius: 1,
                p: 1.5,
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
                gap: 1.5,
              }}
            >
              <Box>
                <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>
                  {t('captcha.info.status', 'Status')}
                </Typography>
                {provisioned ? (
                  <Stack direction="row" spacing={0.5} sx={{ alignItems: 'center' }}>
                    <CheckCircleIcon color="success" fontSize="inherit" />
                    <Typography variant="body2">
                      {t('captcha.info.provisioned', 'Provisioned')}
                    </Typography>
                  </Stack>
                ) : (
                  <Typography variant="body2">
                    {t('captcha.info.notProvisioned', 'Not provisioned')}
                  </Typography>
                )}
              </Box>

              <Box>
                <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>
                  {t('captcha.info.widget', 'Widget')}
                </Typography>
                <Typography variant="body2">
                  {provisioned
                    ? t('captcha.info.widgetDetail', '{{mode}} · {{count}} domain(s)', {
                        mode: widget?.mode ?? 'managed',
                        count: widget?.widgetDomains?.length ?? 0,
                      })
                    : '—'}
                </Typography>
              </Box>

              <Box>
                <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>
                  {t('captcha.fields.siteKey', 'Site key')}
                </Typography>
                {config?.siteKey ? (
                  <>
                    <Stack direction="row" spacing={0.5} sx={{ alignItems: 'center' }}>
                      <Typography
                        variant="body2"
                        sx={{ fontFamily: 'monospace', wordBreak: 'break-all' }}
                      >
                        {config.siteKey}
                      </Typography>
                      <CopyToClipboardButton value={config.siteKey} />
                    </Stack>
                    {provisioned && env.cloudflareAccountId ? (
                      <Link
                        href={`https://dash.cloudflare.com/${env.cloudflareAccountId}/turnstile/widget/${config.siteKey}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        variant="body2"
                      >
                        {t('captcha.manageInCloudflare', 'Manage in Cloudflare')}
                        <OpenInNewIcon
                          fontSize="inherit"
                          sx={{ ml: 0.5, verticalAlign: 'middle' }}
                        />
                      </Link>
                    ) : null}
                  </>
                ) : (
                  <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                    {t('captcha.siteKeyUnset', 'Not provisioned yet')}
                  </Typography>
                )}
              </Box>

              <Box>
                <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>
                  {t('captcha.fields.secret', 'Secret key')}
                </Typography>
                {config?.secretKeySet ? (
                  <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                    {config.secretKeySuffix ?? '••••'}
                  </Typography>
                ) : (
                  <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                    {t('captcha.secretUnset', 'No secret stored')}
                  </Typography>
                )}
              </Box>
            </Box>

            <Stack
              direction="row"
              spacing={2}
              sx={{ alignItems: 'center', flexWrap: 'wrap', rowGap: 1, mt: 2 }}
            >
              <Button
                type="submit"
                variant="contained"
                disabled={saving}
                startIcon={saving ? <CircularProgress size={16} /> : null}
              >
                {t('common.save', 'Save')}
              </Button>
              {provisioned ? (
                <Button
                  onClick={() => void handleRotate()}
                  disabled={rotating}
                  startIcon={rotating ? <CircularProgress size={16} /> : null}
                >
                  {t('captcha.rotateSecret', 'Rotate secret')}
                </Button>
              ) : (
                <Button
                  onClick={() => void handleProvision()}
                  disabled={provisioning}
                  startIcon={provisioning ? <CircularProgress size={16} /> : null}
                >
                  {t('captcha.provision', 'Provision widget')}
                </Button>
              )}
              {configured ? (
                <Button color="error" disabled={removing} onClick={() => setConfirmRemove(true)}>
                  {t('captcha.remove', 'Remove captcha config')}
                </Button>
              ) : null}
            </Stack>
          </Box>
        </>
      )}
      <ConfirmDialog
        open={confirmRemove}
        title={t('captcha.removeTitle', 'Remove captcha config?')}
        body={t(
          'captcha.removeBody',
          "Removes this tenant's Captcha config — site key, secret, and enforce — and cannot be undone.",
        )}
        severity="error"
        confirmLabel={t('captcha.remove', 'Remove captcha config')}
        busy={removing}
        onConfirm={() => void handleRemove()}
        onCancel={() => setConfirmRemove(false)}
      />
    </Paper>
  );
}
